/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [amended C1a] CROSS-CONTEXT SINGLE-FLIGHT FOR THE REFRESH ROTATION.
 *
 *  `http/apiClient.ts` already collapses N simultaneous 401s into one refresh — WITHIN
 *  ONE TAB. `refreshInFlight` is closure state per `createAuthClient`, and a second tab
 *  is a second closure over the same `localStorage`. So two tabs booting together both
 *  read the same refresh token and both POST `/auth/refresh`; one rotates first, the
 *  other presents a token the server has already marked ROTATED, and the server
 *  correctly reads that as a replay and revokes the whole family. Both tabs are signed
 *  out — which is C1a §1, and it is what this file exists to prevent.
 *
 *  ⚠️ IT IS A MUTEX, NOT A SHARED-PROMISE CACHE, AND THAT IS FORCED RATHER THAN CHOSEN.
 *  The winner's ACCESS token lands in its own `sessionStorage`, which is per tab, so
 *  there is no channel by which a waiter could receive it. Building one would mean
 *  putting an access token in `localStorage` — which `web/src/lib/api.test.ts` asserts
 *  against by name, and which widens the XSS blast radius `tokenStore.ts`'s header is
 *  careful about. So a waiter performs its OWN rotation once it holds the lock, with the
 *  token it re-reads at that moment. N tabs cost N sequential, individually valid
 *  rotations behind the bootstrap spinner. That is the whole price.
 *
 *  ── Why not `navigator.locks` ──────────────────────────────────────────────────
 *
 *  It is the natural primitive and it is BANNED here by divergence #28: it is
 *  secure-context-only, so it is `undefined` over plain `http://` to a LAN address —
 *  which is this product's documented demo case (`API_BASE_URL: 'http://192.168.1.5:5210'`
 *  is a literal in `web/src/lib/api.test.ts`). Worse than merely absent: jsdom has no
 *  `navigator.locks` either, so a feature-detected implementation would exercise only
 *  the FALLBACK under test while production exercised the untested primary. Two code
 *  paths, and the wrong one covered.
 *
 *  ── Why not `BroadcastChannel` ─────────────────────────────────────────────────
 *
 *  Not secure-context-gated, and still wrong here. Node's global `BroadcastChannel`
 *  LEAKS INTO the vitest jsdom environment, so a green suite would be measuring
 *  `worker_threads` MessagePort semantics rather than a browser's — divergence #28's
 *  shape with the polarity reversed, and exactly as untrustworthy. It also carries no
 *  state, so a tab that dies mid-refresh leaves nothing behind and liveness needs a
 *  heartbeat protocol anyway: you write the lease logic regardless, just without
 *  durability.
 *
 *  ── Why a `localStorage` lease ─────────────────────────────────────────────────
 *
 *  `localStorage` is ALREADY a hard dependency of this design — the refresh token lives
 *  in it — so a mutex that fails exactly when the credential store fails adds no new
 *  failure mode. It is durable, so a crashed holder leaves visible evidence and expiry
 *  is a real liveness signal rather than a guess. And it works in an insecure context.
 *
 *  ⚠️ THE `storage` EVENT IS AN OPTIMISATION, NEVER THE CORRECTNESS PATH. Correctness
 *  rests on a bounded lease plus a poll, DELIBERATELY: jsdom has one window and filters
 *  the writing window out of `windowsInSameOrigin`, so zero listeners fire and a unit
 *  test structurally cannot prove cross-tab delivery. The one thing the suite cannot see
 *  is therefore the one thing that cannot break the guarantee.
 *
 *  ⚠️ IT NAMES NO BROWSER GLOBAL. Storage and the unload hook are INJECTED by
 *  `tokenStore.ts`. That is not ceremony: it keeps this package's
 *  `no-restricted-properties` credential exemption a list of exactly one file, keeps
 *  `clientCoreIsolation.test.ts` CLAIM 1's needle matching exactly `tokenStore.ts`, and
 *  makes the protocol testable under `@vitest-environment node` with a `Map`-backed fake
 *  — i.e. structurally unable to depend on a jsdom quirk by accident.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** `{ ok: false }` means the area THREW. `value: null` means it answered "nothing there". */
export type LockRead = { ok: true; value: string | null } | { ok: false };

export type LockStorage = {
  read: (key: string) => LockRead;
  /** Returns whether the value is retrievable afterwards, not merely that it did not throw. */
  write: (key: string, value: string) => boolean;
  remove: (key: string) => void;
  /** Cross-context change notification. Returns an unsubscribe; a no-op where unsupported. */
  subscribe: (key: string, onChange: () => void) => () => void;
};

export type RefreshLockOptions = {
  /**
   * ⚠️ DERIVED FROM THE REFRESH KEY BY THE CALLER, NEVER A LITERAL. `web` and `pwa` hold
   * tokens in two different families; one shared lock would serialise two unrelated
   * rotations, and a literal here would be a fifth site naming `pams.*` for
   * `clientCoreIsolation.test.ts` CLAIM 2 to trip over.
   */
  key: string;
  storage: LockStorage;
  /** Given a handler to run when this context goes away. Returns an unsubscribe. */
  onUnload?: (handler: () => void) => () => void;
};

export type RefreshLock = {
  /** Runs `fn` with the cross-context lock held. Never rejects for a lock reason. */
  run: <T>(fn: () => Promise<T>) => Promise<T>;
};

/**
 * ⚠️ THESE FIVE NUMBERS ARE RELATED, NOT INDEPENDENT, AND A UNIT TEST CAUGHT THEM
 * DISAGREEING. `ACQUIRE_TIMEOUT_MS` was a flat 20s while a legitimate holder could keep
 * heartbeating for `MAX_HOLD_MS` — so a waiter abandoned the lock and refreshed UNGUARDED
 * while the holder was still mid-rotation, which is the very race this file prevents,
 * arrived at through the escape hatch. `does NOT rob a slow holder` went red on it.
 *
 * The invariant, now expressed in the arithmetic rather than in a comment: a waiter may
 * only give up once any holder is PROVABLY gone. A wedged holder stops renewing at
 * `MAX_HOLD_MS`, and its last lease dies `LEASE_MS` later — so the timeout must sit beyond
 * their sum, which makes it a backstop against pathological storage rather than a path any
 * real contention can reach.
 */
/** How long one refresh may hold the lock before another context may take it. */
const LEASE_MS = 15_000;
/** Renewed while `fn()` is in flight, so a slow network does not lose the lease. */
const HEARTBEAT_MS = 5_000;
/**
 * A wedged holder stops heartbeating and must yield.
 *
 * ⚠️ [amended C1b §6a] IT USED TO SAY "`fetch` here carries no timeout", AND THAT IS NO
 * LONGER TRUE. `apiClient` bounds a refresh at `REFRESH_TIMEOUT_MS × REFRESH_ATTEMPTS` =
 * 24 000 ms, held across BOTH attempts inside one `run()` — so the worst case now sits 6
 * seconds inside this budget, and a THIRD attempt would put it outside and let a waiter
 * refresh unguarded while a legitimate holder was still mid-retry.
 * `refreshLock.test.ts` asserts that inequality rather than leaving it in this sentence.
 *
 * ⚠️ AND THIS NUMBER STILL DOES NOT MOVE. It bounds a WEDGED holder — a suspended tab,
 * storage gone slow — and a bounded fetch bounds the NETWORK, not the process.
 */
export const MAX_HOLD_MS = 30_000;
/** A lease string unchanged for this long is dead whatever its own clock claims. */
const STEAL_UNCHANGED_MS = LEASE_MS + 2_000;
/**
 * After this, proceed UNGUARDED rather than block a person out of their dashboard for
 * ever. DERIVED, so it cannot drift back under `MAX_HOLD_MS + LEASE_MS` — see above.
 */
const ACQUIRE_TIMEOUT_MS = MAX_HOLD_MS + LEASE_MS + 15_000;
const POLL_MS = 250;
/*
 * `localStorage` has no compare-and-set, so acquisition is write → settle → read-back.
 * The settle is RANDOMISED because two contexts writing in lockstep would otherwise read
 * back in lockstep too; jitter is what makes the residual window vanishingly small. It is
 * only ever "small", never zero — which is why the server-side CAS in
 * `api/src/services/session.service.ts` is the other half of this fix.
 */
const SETTLE_MIN_MS = 3;
const SETTLE_MAX_MS = 15;

type Lease = { holder: string; expiresAt: number };

const parseLease = (raw: string | null): Lease | undefined => {
  if (raw === null) return undefined;
  const separator = raw.lastIndexOf(':');
  if (separator <= 0) return undefined;
  const expiresAt = Number(raw.slice(separator + 1));
  if (!Number.isFinite(expiresAt)) return undefined;
  return { holder: raw.slice(0, separator), expiresAt };
};

/**
 * ⚠️ `crypto.getRandomValues`, NEVER `crypto.randomUUID` — divergence #28: the latter is
 * secure-context-only and Node and jsdom both expose it unconditionally, so no test in
 * this repository could ever see it missing. The `Math.random` fallback is acceptable HERE
 * and nowhere else: a holder-id collision costs one extra rotation and never a credential.
 */
const mintHolderId = (): string => {
  const bytes = new Uint8Array(8);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  }
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

type Acquisition = 'held' | 'unavailable' | 'timeout';

export const createRefreshLock = ({ key, storage, onUnload }: RefreshLockOptions): RefreshLock => {
  const leaseFor = (holder: string): string => `${holder}:${String(Date.now() + LEASE_MS)}`;

  const heldBy = (holder: string): boolean => {
    const current = storage.read(key);
    return current.ok && parseLease(current.value)?.holder === holder;
  };

  const release = (holder: string): void => {
    // ⚠️ Only ever our OWN lease. Removing a successor's would hand the lock to a third
    // context while the second is still inside `fn()`, which is the bug this file prevents.
    if (heldBy(holder)) storage.remove(key);
  };

  const acquire = async (holder: string): Promise<Acquisition> => {
    const deadline = performance.now() + ACQUIRE_TIMEOUT_MS;
    /*
     * ⚠️ TWO CLOCKS, DELIBERATELY. `expiresAt` is compared ACROSS contexts, so it must be
     * `Date.now()` — sound here and only here, because every tab shares one machine clock.
     * "This lease string has not changed for N ms" is a purely LOCAL duration and uses
     * `performance.now()`, so an NTP step cannot make a live holder look dead.
     */
    let lastSeen: string | null | undefined;
    let lastSeenAt = performance.now();

    for (;;) {
      const current = storage.read(key);
      if (!current.ok) return 'unavailable';

      if (current.value !== lastSeen) {
        lastSeen = current.value;
        lastSeenAt = performance.now();
      }

      const lease = parseLease(current.value);
      const free = lease === undefined || Date.now() > lease.expiresAt;
      const wedged = current.value !== null && performance.now() - lastSeenAt >= STEAL_UNCHANGED_MS;

      if (free || wedged) {
        if (!storage.write(key, leaseFor(holder))) return 'unavailable';
        await sleep(SETTLE_MIN_MS + Math.random() * (SETTLE_MAX_MS - SETTLE_MIN_MS));
        const confirmed = storage.read(key);
        if (!confirmed.ok) return 'unavailable';
        if (parseLease(confirmed.value)?.holder === holder) return 'held';
        // Lost the race. Re-evaluate rather than assume the winner is alive.
        continue;
      }

      if (performance.now() >= deadline) return 'timeout';

      let wake: (() => void) | undefined;
      const changed = new Promise<void>((resolve) => {
        wake = resolve;
      });
      const unsubscribe = storage.subscribe(key, () => wake?.());
      await Promise.race([changed, sleep(POLL_MS)]);
      unsubscribe();
    }
  };

  const beat = (holder: string): (() => void) => {
    const until = performance.now() + MAX_HOLD_MS;
    const timer = setInterval(() => {
      // Past MAX_HOLD_MS we stop renewing on purpose: a refresh that has not settled in a
      // minute is wedged, and a wedged holder must become stealable rather than block a
      // whole browser's worth of tabs for ever.
      if (performance.now() >= until || !heldBy(holder)) {
        clearInterval(timer);
        return;
      }
      storage.write(key, leaseFor(holder));
    }, HEARTBEAT_MS);

    return () => {
      clearInterval(timer);
    };
  };

  return {
    run: async <T>(fn: () => Promise<T>): Promise<T> => {
      const holder = mintHolderId();

      /*
       * ──────────────────────────────────────────────────────────────────────────────
       *  ⚠️ [amended C5-FIX] THE UNLOAD HOOK IS ARMED BEFORE `acquire()`, AND THE RELEASE
       *  COVERS EVERY PATH — BECAUSE THIS LOCK USED TO ORPHAN THE LEASE IT HAD JUST WRITTEN.
       *
       *  `acquire()` WRITES at the top of its `free || wedged` branch and only then settles,
       *  reads back and decides (see `:216`–`:222`). So between the write and a verdict there
       *  is a real window in which a 15 s lease exists on disk. The hook used to be armed
       *  only AFTER the verdict was `'held'`, and the releasing `finally` was entered only on
       *  that same path — which left two leaks, and NEITHER had a test:
       *
       *    · a document destroyed inside the 3–15 ms settle had written a lease with no hook
       *      registered to release it; and
       *    · `if (!confirmed.ok) return 'unavailable'` returned through the early
       *      `return fn()` below, which carried no `finally` at all — so a lease written on
       *      that path was never released by anybody, by any route, ever.
       *
       *  ⚠️ AN ORPHAN IS NOT A LOST REFRESH, IT IS A BLOCKED BOOT, AND THAT IS WHY IT WAS
       *  WORTH FINDING. The next context to start reads a lease that is neither free (its
       *  `expiresAt` is 15 s out) nor `wedged` (`lastSeenAt` is seeded from that context's OWN
       *  `performance.now()`, so `STEAL_UNCHANGED_MS` can never bind before expiry), so it
       *  sits in the 250 ms poll at `:232` and issues NO network request at all. `pwa`'s
       *  `AuthProvider` gates its entire tree on that one await, so the officer gets
       *  `Loading…` and nothing else until the orphan expires.
       *
       *  MEASURED, from the browser suite's own Playwright trace: `dutyEvents.spec.ts`'s §7a
       *  reloads a page offline and closes it ~14 ms later, and the fresh page then made six
       *  requests for its shell and not one `POST /auth/refresh` for the following 8 s.
       *
       *  ⚠️ AND IT IS SAFE ON THE PATHS THAT HOLD NOTHING — which is `release`'s own
       *  property rather than a new rule. It removes the key only while `heldBy(holder)`, so
       *  a `'timeout'` that never wrote, and a settle this context LOST to a successor, both
       *  remove NOTHING. Only a lease still carrying our own holder id is ever cleared, so a
       *  successor mid-`fn()` cannot have the lock taken from under it.
       * ──────────────────────────────────────────────────────────────────────────────
       */
      const stopUnloadHook = onUnload?.(() => {
        release(holder);
      });
      let stopHeartbeat: (() => void) | undefined;

      try {
        const outcome = await acquire(holder);
        // ⚠️ EVERY FAILURE PATH RUNS `fn` ANYWAY. A mutex that can deny a person their own
        // dashboard is worse than the race it prevents; `unavailable` and `timeout` both
        // degrade to exactly the behaviour that shipped before this file existed.
        if (outcome === 'held') stopHeartbeat = beat(holder);
        return await fn();
      } finally {
        stopHeartbeat?.();
        stopUnloadHook?.();
        release(holder);
      }
    },
  };
};
